import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import * as constants from 'eventbrite_design_system/globalHeader/constants';
import IconButton from 'eventbrite_design_system/iconButton/IconButton';
import Icon from 'eventbrite_design_system/icon/Icon';
import MagnifyingGlassChunkySvg from 'eventbrite_design_system/iconography/icons/MagnifyingGlassChunky';
import PlusChunkySvg from 'eventbrite_design_system/iconography/icons/PlusChunky';
import { getNarrowMenuInfo } from 'eventbrite_design_system/globalHeader/utils';

import { setWindowLocation } from 'js-utils/http';

import {
    getCreateEventCTA,
    getHelpMenu,
    getLogoInfo,
    getUserMenuInfo,
    getNonUserQuickLinks,
    getOrganizeMenu,
    FALLBACK_SUGGESTIONS,
    ENV_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
} from '@eb/site-header';

import { gettext } from '@eb/i18n';
// import MediaQuery from '../MediaQuery';
import GlobalHeaderLogo from './GlobalHeaderLogo';
import GlobalHeaderSearch, { getSearchInfo } from './GlobalHeaderSearch';
import {
    GlobalHeaderQuickLinks,
    GlobalHeaderCallToAction,
    GlobalHeaderMenu,
    GlobalHeaderMenus,
} from './GlobalHeaderNavigationBar';
import {
    getLikesQuickLinks,
    getMyTicketsQuickLinks,
    getBugQuickLinks,
} from './core-js-global-copies';

import { FEATURE_FLAGS_PROPTYPES } from '../../constants/propTypes';

import './ConsumerHeaderIndex.scss';

class ConsumerHeader extends React.Component {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        onSearchSelect: PropTypes.func,
        onAdditionalMenuTextItemSelect: PropTypes.func,
        handleSearch: PropTypes.func,
        onSearchFocus: PropTypes.func,
        isHomePage: PropTypes.bool,
        isSearchPage: PropTypes.bool,
        isMobile: PropTypes.bool,
        hideSearchBar: PropTypes.bool,
        id: PropTypes.string,
        featureFlags: FEATURE_FLAGS_PROPTYPES.isRequired,
    };

    static defaultProps = {
        id: constants.DEFAULT_ID,
        onSearchSelect: setWindowLocation,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchQuery: props.env.searchQuery || '',
            searchSuggestions: FALLBACK_SUGGESTIONS,
            showSearch: false,
        };
    }

    _handleCancelClick = () => {
        this.setState({
            showSearch: false,
        });
    };

    _handleSearchClick = () => {
        this.setState({
            showSearch: true,
        });
    };

    _handleOnAdditionalMenuTextItemSelect = (value, index) => {
        if (this.props.onAdditionalMenuTextItemSelect) {
            this.props.onAdditionalMenuTextItemSelect(value, index);
        }
    };

    _getHeaderLinks = () => {
        const {
            env: { ebDomain, helpUrls, loginUrl, organizeUrls, serverUrl },
            request: { path: requestPath },
            user: { isAuthenticated, canCreateEvents, publicId },
            featureFlags,
        } = this.props;

        const likesLinks = getLikesQuickLinks(
            serverUrl,
            ebDomain,
            requestPath,
            publicId,
        );
        const myTicketsLinks = getMyTicketsQuickLinks(
            serverUrl,
            ebDomain,
            requestPath,
            publicId,
        );
        const callToActionInfo = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            canCreateEvents,
        );
        const additionalMenus = [
            getOrganizeMenu(organizeUrls, requestPath),
            getHelpMenu(helpUrls, requestPath),
        ];
        const ctaIcon = <Icon size="small" type={<PlusChunkySvg />} />;

        let linkComponents = [];

        // on the search page, show britelings a link to report a bug
        if (featureFlags.showReportBugLinkInHeader) {
            linkComponents = [
                <GlobalHeaderQuickLinks
                    key="bugLinks"
                    links={getBugQuickLinks()}
                    shouldShowIcon={true}
                />,
            ];
        }

        linkComponents = [
            ...linkComponents,

            <GlobalHeaderCallToAction
                key="createEventLink"
                linkInfo={callToActionInfo}
                iconType={ctaIcon}
                shouldShowIcon={true}
                iconColor="grey-800"
            />,
            <GlobalHeaderQuickLinks
                key="likesLinks"
                links={likesLinks}
                shouldShowIcon={true}
            />,
            <GlobalHeaderQuickLinks
                key="myTicketsLinks"
                links={myTicketsLinks}
                shouldShowIcon={true}
            />,
        ];

        if (!isAuthenticated) {
            const nonUserQuickLinks = getNonUserQuickLinks(
                requestPath,
                isAuthenticated,
                loginUrl,
            );

            linkComponents = [
                <GlobalHeaderMenus
                    key="additionalMenus"
                    additionalMenus={additionalMenus}
                    onAdditionalMenuTextItemSelect={
                        this._handleOnAdditionalMenuTextItemSelect
                    }
                />,
                <GlobalHeaderCallToAction
                    key="createEventLinkNotAuth"
                    linkInfo={callToActionInfo}
                    shouldShowIcon={false}
                />,
                <GlobalHeaderQuickLinks
                    key="nonUserQuickLinks"
                    links={nonUserQuickLinks}
                    shouldShowIcon={false}
                />,
            ];
        }

        return <div className="consumer-header__links">{linkComponents}</div>;
    };

    _getGlobalHeaderSearch = (searchInfo) => {
        const {
            id,
            isSearchPage,
            onSearchSelect,
            handleSearch,
            hideSearchBar,
        } = this.props;

        if (!isSearchPage && !hideSearchBar) {
            return (
                <GlobalHeaderSearch
                    id={`${id}-desktop-search`}
                    searchInfo={searchInfo}
                    onSubmit={handleSearch}
                    onSelect={onSearchSelect}
                />
            );
        }

        return <div className="consumer-header__search__placeholder" />;
    };

    _getMobileHeaderLinks = () => {
        const {
            user,
            env: {
                adminServerUrl,
                ebDomain,
                loginUrl,
                logoutUrl,
                helpUrls,
                organizeUrls,
                isManageOverviewActive,
                serverUrl,
            },
            request: { path: requestPath },
            hideSearchBar,
            featureFlags,
        } = this.props;

        const { canCreateEvents, isAuthenticated } = user;
        const additionalMenus = [
            getOrganizeMenu(organizeUrls, requestPath),
            getHelpMenu(helpUrls, requestPath),
        ];
        const userMenuInfo = getUserMenuInfo({
            serverUrl,
            adminServerUrl,
            logoutUrl,
            requestPath,
            user,
            isManageOverviewActive,
            showBrowseEvents: hideSearchBar,
            featureFlags,
        });

        const nonUserQuickLinks = getNonUserQuickLinks(
            requestPath,
            isAuthenticated,
            loginUrl,
        );
        const callToActionInfo = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            canCreateEvents,
        );

        const narrowMenuInfo = getNarrowMenuInfo(
            userMenuInfo,
            additionalMenus,
            [],
            callToActionInfo,
            [],
            true,
        );

        let narrowMenu;

        if (narrowMenuInfo) {
            const className = classNames(
                'eds-global-header__narrow-menu',
                'eds-show-down-mn',
            );

            narrowMenu = [narrowMenuInfo].map((menuInfo) => (
                <span key={menuInfo.label} className={className}>
                    <GlobalHeaderMenu
                        menuInfo={menuInfo}
                        data-spec="global-header-narrow-menu"
                    />
                </span>
            ));
        }

        return (
            <div className="consumer-header__links">
                {narrowMenu}
                <GlobalHeaderQuickLinks
                    links={nonUserQuickLinks}
                    shouldShowIcon={false}
                />
            </div>
        );
    };

    render() {
        const {
            id,
            env,
            user,
            request,
            hideSearchBar,
            // isMobile,
            isHomePage,
            isSearchPage,
            onSearchSelect,
            handleSearch,
            featureFlags,
        } = this.props;
        const { searchQuery, searchSuggestions, showSearch } = this.state;
        const { serverUrl, adminServerUrl, logoutUrl } = env;
        const { path: requestPath } = request;
        const logoInfo = getLogoInfo(serverUrl, requestPath, {
            iconWidth: '110px',
        });
        const searchInfo = getSearchInfo(
            serverUrl,
            searchQuery,
            searchSuggestions,
            hideSearchBar,
        );
        const userMenuInfo = getUserMenuInfo({
            serverUrl,
            adminServerUrl,
            logoutUrl,
            requestPath,
            user,
            showBrowseEvents: hideSearchBar,
            featureFlags,
        });

        const searchMobileClasses = classNames(
            'consumer-header__mobile-search',
            {
                'eds-hide': !showSearch,
                'eds-fx--fade-move-in': showSearch,
            },
        );

        return (
            <header
                id={id}
                className={`consumer-header ${
                    isHomePage ? '' : 'consumer-header__not-homepage'
                }`}
                data-spec="consumer-header"
            >
                <a
                    className="consumer-header__skip-links eds-is-hidden-accessible"
                    href="#skip-heading"
                >
                    {gettext('Skip Main Navigation')}
                </a>
                <div className="consumer-header__main" data-role="header">
                    {/* Switching to eds-show-up/down for now to fix the header CSS issues on mobile */}
                    {/* <MediaQuery breakpoint="isMediumUp" forceInitialRender={!isMobile}> */}
                    <div
                        className="consumer-header__content consumer-header__desktop eds-show-up-md"
                        data-spec="consumer-header-desktop"
                    >
                        <GlobalHeaderLogo info={logoInfo} />
                        {this._getGlobalHeaderSearch(searchInfo)}
                        {this._getHeaderLinks()}
                        <GlobalHeaderMenus userMenuInfo={userMenuInfo} />
                    </div>
                    {/* </MediaQuery> */}

                    {/* Switching to eds-show-up/down for now to fix the header CSS issues on mobile */}
                    {/* <MediaQuery breakpoint="isMediumDown" forceInitialRender={isMobile}> */}
                    <div
                        className="consumer-header__content consumer-header__mobile eds-show-down-mn eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1"
                        data-spec="consumer-header-mobile"
                    >
                        <div className="consumer-header__mobile-links eds-align--space-between">
                            <GlobalHeaderLogo info={logoInfo} />
                            <div className="eds-align--center-vertical">
                                {!isSearchPage && !hideSearchBar && (
                                    <div className="consumer-header__mobile-search-button">
                                        <IconButton
                                            iconType={
                                                <MagnifyingGlassChunkySvg />
                                            }
                                            title={gettext('Search button')}
                                            iconColor="grey-600"
                                            onClick={this._handleSearchClick}
                                        />
                                    </div>
                                )}
                                {this._getMobileHeaderLinks()}
                            </div>
                        </div>
                        <div className={searchMobileClasses}>
                            <GlobalHeaderSearch
                                id={`${id}-mobile-search`}
                                searchInfo={searchInfo}
                                onSubmit={handleSearch}
                                onSelect={onSearchSelect}
                            />
                            <button
                                className="consumer-header__cancel-link eds-btn eds-btn--button eds-btn--link"
                                onClick={this._handleCancelClick}
                                type="button"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                    {/* </MediaQuery> */}
                </div>

                <span id="skip-heading" className="eds-is-hidden-accessible">
                    {gettext('Page Content')}
                </span>
            </header>
        );
    }
}

export default ConsumerHeader;
